import { ReactNode } from 'react';
import { ComplexTooltip, TooltipPlacement } from './tooltip';
import { formatMsFull, formatMsFullNoHours, naturalShortDate, relativeDate } from '../../libs/utils';
import { t } from 'i18next';

// Color constants for time display
export const TIME_COLOR_UNDER = '#66ff00';
export const TIME_COLOR_OVER = '#ff6600';

// Base run time data (always required)
interface IRunTimeDataBase {
  clearTimeMs: number; // actual clear time
  keystoneTimeMs: number; // par time (computed if not provided)
}

// Run time data with completion date
export interface IRunTimeDataWithDate extends IRunTimeDataBase {
  completedAt: string; // ISO date string
}

// Run time data without completion date
export interface IRunTimeDataWithoutDate extends IRunTimeDataBase {
  completedAt?: undefined;
}

// Union type for run data
export type IRunTimeData = IRunTimeDataWithDate | IRunTimeDataWithoutDate;

// Base props shared by both variants
interface IRunTimeTooltipContentPropsBase {
  prefix?: ReactNode; // e.g., "BEST ARAK: Fortified +12"
  suffix?: ReactNode; // arbitrary content after time
  useShortFormat?: boolean; // default true, uses formatMsFullNoHours
  className?: string;
}

// Props when run has completedAt - date display options allowed
interface IRunTimeTooltipContentPropsWithDate extends IRunTimeTooltipContentPropsBase {
  run: IRunTimeDataWithDate;
  showRelativeDate?: boolean; // "13 weeks ago"
  showAbsoluteDate?: boolean; // "Oct 16 2025 7:40 PM PDT"
}

// Props when run lacks completedAt - date display options not allowed
interface IRunTimeTooltipContentPropsWithoutDate extends IRunTimeTooltipContentPropsBase {
  run: IRunTimeDataWithoutDate;
  showRelativeDate?: never;
  showAbsoluteDate?: never;
}

// Union type enforces: date options only available when completedAt is present
export type IRunTimeTooltipContentProps =
  | IRunTimeTooltipContentPropsWithDate
  | IRunTimeTooltipContentPropsWithoutDate;

export type IRunTimeTooltipProps = IRunTimeTooltipContentProps & {
  children: ReactNode;
  placement?: TooltipPlacement; // default 'top'
  hideTooltip?: boolean;
  tag?: string; // default 'span'
};

/**
 * Calculate percentage over/under time
 */
export function calculateRunTimeDeltaPercentage(run: IRunTimeData): string {
  if (run.keystoneTimeMs <= 0) {
    return '0.0';
  }

  return ((100 * Math.abs(run.keystoneTimeMs - run.clearTimeMs)) / run.keystoneTimeMs).toFixed(1);
}

/**
 * Pure content component for embedding in any tooltip.
 * Displays run time information with configurable options.
 */
export function RunTimeTooltipContent(props: IRunTimeTooltipContentProps) {
  const { run, useShortFormat = true } = props;
  const timeRemainingMs = run.keystoneTimeMs - run.clearTimeMs;
  const isUnder = timeRemainingMs >= 0;
  const color = isUnder ? TIME_COLOR_UNDER : TIME_COLOR_OVER;
  const timeMs = Math.abs(timeRemainingMs);
  const formattedTime = useShortFormat ? formatMsFullNoHours(timeMs) : formatMsFull(timeMs);
  const percentage = calculateRunTimeDeltaPercentage(run);

  // Only show date fields when completedAt is present
  const hasCompletedAt = run.completedAt !== undefined;

  return (
    <span className={props.className}>
      {props.prefix}
      <p style={{ color }}>
        <b>
          {t(isUnder ? 'mplus-runs:under-by-percent-short' : 'mplus-runs:over-by-percent-short', {
            time: formattedTime,
            percent: percentage
          })}
        </b>
      </p>
      {hasCompletedAt && props.showAbsoluteDate && <p>{naturalShortDate(run.completedAt)}</p>}
      {hasCompletedAt && props.showRelativeDate && <p>{relativeDate(run.completedAt)}</p>}
      {props.suffix}
    </span>
  );
}

/**
 * Wrapper component that applies ComplexTooltip to children.
 * Use this for hover tooltips.
 */
export function RunTimeTooltip(props: IRunTimeTooltipProps) {
  const { children, placement = 'top', hideTooltip, tag = 'span', ...contentProps } = props;

  return (
    <ComplexTooltip
      tag={tag}
      placement={placement}
      hideTooltip={hideTooltip}
      tooltip={
        <div className="text-white">
          <RunTimeTooltipContent {...contentProps} />
        </div>
      }
    >
      {children}
    </ComplexTooltip>
  );
}
